import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { Router, RouterModule } from '@angular/router';
import { BasePaneComponent, IBasePaneTab } from '@app/_v3/components/base-pane.component';
import { LocaleSharedModule } from '@app/i18n/locale-shared.module';
import { ContentCardModule } from '@app/library/content-card/content-card.module';
import { LibraryElementsModule } from '@app/library/library-elements.module';
import { ERedPointsEvents } from '@app/store/models';
import { MobileModalModule } from '@core/components/mobile-modal/mobile-modal.module';
import { ModalModule } from '@core/components/modal/modal.module';
import { NoContentStubComponent } from '@core/components/no-content-stub/no-content-stub.component';
import { RedPointDirective } from '@core/components/red-points/red-point/red-point.directive';
import { SpinnersModule } from '@core/components/spinners/spinners.module';
import { AuthService, BillingService } from '@core/services';
import { remoteConfigDefaults } from '@core/services/remote-config/constants';
import { RemoteConfigService } from '@core/services/remote-config/remote-config.service';
import { LibraryRouter } from '@models/library.router';
import { IconComponent } from '@shared/icon/icon.component';
import { StatusBadgeComponent } from '@shared/layouts/status-badge/status-badge.component';
import { Content, FindContentStrategyEnum, ModerationStatusEnum, PublishStatusEnum } from 'lingo2-models';
import { InViewportModule } from 'ng-in-viewport';
import { OnUiButtonModule } from 'onclass-ui';
import { takeUntil } from 'rxjs/operators';
import { LibraryContentMiniComponent } from './library-content-mini/library-content-mini.component';
@Component({
  selector: 'app-my-library-content',
  standalone: true,
  imports: [
    CommonModule,
    RouterModule,
    LocaleSharedModule,
    LibraryElementsModule,
    ContentCardModule,
    SpinnersModule,
    OnUiButtonModule,
    ModalModule,
    MobileModalModule,
    RedPointDirective,
    StatusBadgeComponent,
    NoContentStubComponent,
    IconComponent,
    InViewportModule,
    LibraryContentMiniComponent,
  ],
  templateUrl: './my-library-content.component.html',
  styleUrls: ['./my-library-content.component.scss', '../../panes/filter-content/filter-content.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MyLibraryContentComponent extends BasePaneComponent<IBasePaneTab> {
  protected readonly router = inject(Router);
  protected readonly authService = inject(AuthService);
  protected readonly billingService = inject(BillingService);
  protected readonly remoteConfigService = inject(RemoteConfigService);

  public override tabs: IBasePaneTab[] = [
    {
      id: 1,
      code: PublishStatusEnum.published,
      title: 'publish-status.published',
      stub: 'my-library.no-published-lesson',
      stub_guest: 'my-library.need-authorization',
    },
    {
      id: 2,
      code: PublishStatusEnum.private,
      title: 'publish-status.private',
      stub: 'my-library.no-private-lesson',
      stub_guest: 'my-library.need-authorization',
    },
    {
      id: 3,
      code: PublishStatusEnum.draft,
      title: 'publish-status.draft',
      stub: 'my-library.no-draft-lesson',
      stub_guest: 'my-library.need-authorization',
    },
    {
      id: 4,
      code: PublishStatusEnum.moderation,
      title: 'publish-status.moderation',
      stub: 'my-library.no-moderation-lesson',
      stub_guest: 'my-library.need-authorization',
    },
    {
      id: 5,
      code: PublishStatusEnum.archive,
      title: 'publish-status.archive',
      stub: 'my-library.no-archive-lesson',
      stub_guest: 'my-library.need-authorization',
    },
  ];

  public openedLesson: Content;
  private remoteConfig;

  /*
   */
  override ngOnInit(): void {
    super.ngOnInit();

    this.remoteConfigService.config$.pipe(takeUntil(this.destroyed$)).subscribe({
      next: (config) => {
        this.remoteConfig = config;
        this.detectChanges();
      },
      error: () => {
        this.remoteConfig = remoteConfigDefaults;
        this.detectChanges();
      },
    });
  }

  override ngAfterViewInit(): void {
    super.ngAfterViewInit();
    this.onSelectTab(this.tabs[0]);
  }

  /*
   */
  public onCreateLesson(): void {
    const options = {
      caller: 'app-my-library-content',
      reason: 'create lesson',
    };

    const proceed = () => {
      if (this.remoteConfig?.createLesson__paywall) {
        this.billingService.showPaywall(() => this.goToConstructor(), options);
      } else {
        this.goToConstructor();
      }
    };

    if (this.remoteConfig?.createLesson__auth) {
      this.authService.showAuthModal(() => proceed(), options);
    } else {
      proceed();
    }
  }

  /*
   */
  protected goToConstructor(): void {
    try {
      const url = this.router.serializeUrl(this.router.createUrlTree(['/constructor']));
      window.open(url, '_blank');
    } catch {
      void this.router.navigate(['/constructor']);
    }
  }

  /*
   */
  public onClickedCard() {
    this.resetPane();
    this.loadPane();
  }

  /*
   */
  public onToggleLessonInModal(content: Content = null): void {
    this.openedLesson = content;
    this.detectChanges();
  }

  /*
   */
  public get isOnModeration(): boolean {
    return this.openedLesson?.moderation_status === ModerationStatusEnum.default;
  }

  /*
   */
  public get isDeclined(): boolean {
    return (
      this.openedLesson?.moderation_status === ModerationStatusEnum.softDeclined ||
      this.openedLesson?.moderation_status === ModerationStatusEnum.hardDeclined
    );
  }

  /*
   */
  protected override redPointEvent(tab: IBasePaneTab): ERedPointsEvents {
    const redPointMap: Record<string, ERedPointsEvents> = {
      [PublishStatusEnum.published]: ERedPointsEvents.my_lesson_published,
      [PublishStatusEnum.moderation]: ERedPointsEvents.my_lesson_moderation,
    };

    return redPointMap[tab?.code] ?? null;
  }

  /*
   */
  protected override setupFilter(tab: IBasePaneTab): void {
    const tabCode = tab.code as PublishStatusEnum;

    const contentFilter: any = {
      publish_status: [tabCode],
      use: FindContentStrategyEnum.owned,
    };

    if (tabCode === PublishStatusEnum.moderation) {
      contentFilter.moderation_status = [
        ModerationStatusEnum.unmoderated,
        ModerationStatusEnum.default,
        ModerationStatusEnum.softDeclined,
        ModerationStatusEnum.hardDeclined,
      ];
      contentFilter.publish_status = [PublishStatusEnum.published];
    } else if (tabCode === PublishStatusEnum.published) {
      contentFilter.moderation_status = [ModerationStatusEnum.approved];
    }

    this._pane = {
      ...this._pane,
      loaded: false,
      content: [],
      contentFilter,
    };
  }

  /*
   */
  protected override createNewEntity(): Content {
    return null;
  }

  /*
   */
  protected override getEntityRoute(_entity: any): string {
    return LibraryRouter.contentRouteUniversal(_entity);
  }

  /*
   */
  public trackContentByFn(index: number, item: Content): string {
    return item.id;
  }

  /*
   */
  public goSignup(event: Event): void {
    if (this.isGuest) {
      this.authService.showAuthModal();
    }
  }
}
